import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Form, Button, Container, Row, Col, Modal } from 'react-bootstrap';
import { Eye, EyeOff } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation, Navigate } from 'react-router-dom';
import loginLanding from 'assets/images/logo/PMR_BG.png';
import acmeHealthLogo from 'assets/images/logo/acme_health_new_logo_2.png';
import { loginUser, userSignUp, UserTypesGet, CountryCodesGet, sharedReportsValidateCode, validateUserEmail, generateOneTimeLoginLink, resetPasswordEmailNotification, resetUserPassword, validateResetPasswordToken, validateTokenAccessLink, passkeyLogin, setLoginDetails, setMenuItem, setFamilyAccounts, setToken } from 'apiServices/reducer/apiService';
//import 'bootswatch/dist/lux/bootstrap.min.css';
import './Login.css';
import { MdCheckCircleOutline, MdWarningAmber, MdOutlineCancel } from 'react-icons/md';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner'
import PasskeyLoginButton, { PasskeyPersonKeyIcon } from '../../components/passkey/PasskeyLoginButton';
import PasskeySetupModal from '../../components/passkey/PasskeySetupModal';
import usePasskey from '../../hooks/usePasskey';
import { accountHasPasskey, getDeviceLabel, getPasskeyApiError, isPasskeyCancelled, listPasskeys, markPasskeyOfferAfterLogin, clearPasskeyOfferAfterLogin, normalizeLoginSession, peekAccountHasPasskey, pickPasskeyToken, pickPasskeyUserId } from '../../services/passkeyService';
import { showPmrError, showPmrSuccess } from '../../utils/swalUtils';
import { showNotificationIfAllowed } from '../../utils/pushNotification';
// import DatePicker from "react-datepicker";
// import "react-datepicker/dist/react-datepicker.css";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import "flatpickr/dist/themes/material_green.css";
import { CTDatePickerRange } from "components";
import { Calendar } from "react-feather"; 
import PmrSelect from '../../components/controls/dropdown/PmrSelect';

const SIGNUP_GENDER_OPTIONS = [
    { value: '0', label: 'Male' },
    { value: '1', label: 'Female' },
    { value: '2', label: 'Other' },
];

const hasPersistedSession = (login, token) => {
    if (typeof token === 'string' && token.trim()) return true;
    if (login?.Token != null && String(login.Token).trim()) return true;
    if (login?.token != null && String(login.token).trim()) return true;
    return false;
};

// `<base href="/">` makes href="#" open "/#" in a new tab. That mounts Login
// and used to wipe the shared session. If a session is already stored, send
// the new tab into the app instead of logging every tab out.
const getResumeAppPath = (login, token) => {
    if (typeof window === 'undefined') return '';
    // A brand-new visit (tab closed, site opened again) must stay on Login.
    // Auto-resume only after the app is already open, such as the Back button.
    if (!window.__PMR_SPA_READY__) return '';
    const path = window.location.pathname || '/';
    if (path !== '/' && path !== '') return '';
    if (!hasPersistedSession(login, token)) return '';
    const params = new URLSearchParams(window.location.search);
    const menuId = params.get('menuId');
    const menu = params.get('menu');
    if (params.get('id')) return `/landing-page?id=${encodeURIComponent(params.get('id'))}`;
    if (menuId) return `/landing-page?menuId=${encodeURIComponent(menuId)}`;
    if (menu) return `/landing-page?menu=${encodeURIComponent(menu)}`;
    try {
        const lastAppPath = sessionStorage.getItem('pmr_last_app_path') || '';
        const allowed = ['/landing-page', '/admin-landing-page', '/provider-landing-page', '/user-landing-page', '/access-landing-page', '/reports', '/blog', '/patient-summary'];
        if (allowed.some((prefix) => lastAppPath === prefix || lastAppPath.startsWith(`${prefix}?`) || lastAppPath.startsWith(`${prefix}/`))) {
            return lastAppPath;
        }
    } catch (e) {
        // ignore
    }
    return '/landing-page';
};

const findFirstArrayInObject = (obj) => {
    if (!obj || typeof obj !== 'object') return null;
    for (const value of Object.values(obj)) {
        if (Array.isArray(value)) return value;
    }
    // one-level deep (common: { data: { Table: [...] } })
    for (const value of Object.values(obj)) {
        if (value && typeof value === 'object') {
            for (const nested of Object.values(value)) {
                if (Array.isArray(nested)) return nested;
            }
        }
    }
    return null;
};

const normalizeUserTypes = (payload) => {
    if (Array.isArray(payload)) return payload;

    const rows =
        payload?.Table ??
        payload?.table ??
        payload?.Data ??
        payload?.data?.Table ??
        payload?.data?.table ??
        findFirstArrayInObject(payload);

    return Array.isArray(rows) ? rows : [];
};

const normalizeCountryCodeOptions = (payload) => {
    const rows =
        payload?.Table ??
        payload?.table ??
        payload?.Data ??
        payload?.data?.Table ??
        payload?.data?.table ??
        (Array.isArray(payload) ? payload : null) ??
        findFirstArrayInObject(payload) ??
        [];

    if (!Array.isArray(rows)) return [];

    const options = rows
        .map((row) => {
            // API can return: ["+1", "+91"] or [{...}]
            if (typeof row === 'string' || typeof row === 'number') {
                const digits = String(row).replace(/[^\d]/g, '');
                if (!digits) return null;
                const value = `+${digits}`;
                return { value, label: value };
            }

            const codeRaw =
                row?.country_code ??
                row?.CountryCode ??
                row?.dial_code ??
                row?.DialCode ??
                row?.phone_code ??
                row?.PhoneCode ??
                row?.code ??
                row?.Code;

            const nameRaw =
                row?.country_name ??
                row?.CountryName ??
                row?.name ??
                row?.Name;

            if (codeRaw == null || codeRaw === '') return null;
            const digits = String(codeRaw).replace(/[^\d]/g, '');
            if (!digits) return null;
            const value = `+${digits}`;
            const label = value; // keep compact in UI
            return { value, label };
        })
        .filter(Boolean);

    // de-dupe and sort by numeric code
    const uniq = new Map();
    for (const o of options) uniq.set(o.value, o);
    return Array.from(uniq.values()).sort((a, b) => Number(a.value.slice(1)) - Number(b.value.slice(1)));
};

const formatFullPhone = (countryCode, phone) => {
    const cc = String(countryCode || '').trim() || '+1';
    const digits = String(phone || '').replace(/[^0-9]/g, '').slice(0, 10);
    if (!digits) return '';
    return `${cc}${digits}`;
};

const Auth = (props) => {
    const [showPassword, setShowPassword] = useState(false);
    const loginEmailRef = useRef(null);
    const loginPasswordRef = useRef(null);
    const [showSignUpPassword, setShowSignUpPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSignUp, setIsSignUp] = useState(false);
    const [roles, setRoles] = useState([]);
    const [countryCodeOptions, setCountryCodeOptions] = useState([]);
    const [rememberMe, setRememberMe] = useState(false);
    const userTypes = useSelector((state) => state.apiService.UserTypesGet);
    const [showAccessReports, setShowAccessReports] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [warningMessage, setWarningMessage] = useState('');
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [emailAction, setEmailAction] = useState('');
    const [emailForm, setEmailForm] = useState({ email: '' });
    const [emailError, setEmailError] = useState('');
    const [loginSubmitted, setLoginSubmitted] = useState(false);
    const [loginValidation, setLoginValidation] = useState({
        email: { isValid: true, message: '' },
        password: { isValid: true, message: '' },
        credentials: { isValid: true, message: '' }
    });
    const [showResetPassword, setShowResetPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
    const [resetPasswordForm, setResetPasswordForm] = useState({
        newPassword: '',
        confirmPassword: ''
    });
    const [resetPasswordValidation, setResetPasswordValidation] = useState({
        newPassword: { isValid: true, message: '' },
        confirmPassword: { isValid: true, message: '' }
    });
    const [resetPasswordSubmitted, setResetPasswordSubmitted] = useState(false);
    const [showOneTimeAccessForm, setShowOneTimeAccessForm] = useState(false);
    const [oneTimeAccessForm, setOneTimeAccessForm] = useState({ email: '' });
    const [oneTimeAccessValidation, setOneTimeAccessValidation] = useState({
        email: { isValid: true, message: '' }
    });
    const [oneTimeAccessSubmitted, setOneTimeAccessSubmitted] = useState(false);
    const [showErrorReportForm, setShowErrorReportForm] = useState(false);
    const [expireMessage, setExpireMessage] = useState("")

    const [loginForm, setLoginForm] = useState({
        user_name: '',
        password: '',
    });
    const dispatch = useDispatch();
    const location = useLocation();
    const { token } = useParams(); // Get token from URL path
    const reduxLogin = useSelector((state) => state.apiService.login);
    const reduxToken = useSelector((state) => state.apiService.token);
    console.log(token, "token");

    const [signUpForm, setSignUpForm] = useState({
        firstName: '',
        lastName: '',
        gender: '',
        phone_number: '',
        phone_country_code: '+1',
        dob: '',
        email: '',
        password: '',
        confirmPassword: '',
        role: 'User',
        usertype_id: '2' // Adding default usertype_id for User role
    });

    const [userIDforResetPassword, setUserIDforResetPassword] = useState();

    const [sharedReportsForm, setSharedReportsForm] = useState({
        share_code: '',
        dob: '',
        email_address: '',
        usertype_id: '',
    });

    const [validationError, setValidationError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const [pendingSignupCredentials, setPendingSignupCredentials] = useState(null);
    const [pendingLoginSession, setPendingLoginSession] = useState(null);
    const [showPasskeySetupModal, setShowPasskeySetupModal] = useState(false);
    const {
        supported: passkeySupported,
        supportMessage: passkeySupportMessage,
        busy: passkeyBusy,
        error: passkeyError,
        setError: setPasskeyError,
        register: registerPasskeyOnDevice,
    } = usePasskey();
    const [emailHasPasskey, setEmailHasPasskey] = useState(false);

    const [signUpValidation, setSignUpValidation] = useState({
        firstName: { isValid: true, message: '' },
        lastName: { isValid: true, message: '' },
        gender: { isValid: true, message: '' },
        phone_number: { isValid: true, message: '' },
        dob: { isValid: true, message: '' },
        email: { isValid: true, message: '' },
        password: { isValid: true, message: '' },
        confirmPassword: { isValid: true, message: '' },
        role: { isValid: true, message: '' }
    });

    const [signUpSubmitted, setSignUpSubmitted] = useState(false);

    // Helper function to reset sign-up validation state
    const resetSignUpValidation = () => {
        setSignUpValidation({
            firstName: { isValid: true, message: '' },
            lastName: { isValid: true, message: '' },
            gender: { isValid: true, message: '' },
            phone_number: { isValid: true, message: '' },
            dob: { isValid: true, message: '' },
            email: { isValid: true, message: '' },
            password: { isValid: true, message: '' },
            confirmPassword: { isValid: true, message: '' },
            role: { isValid: true, message: '' }
        });
    };

    /** Flatpickr passes selectedDates[]; normalize to Date or '' so cleared DOB is not truthy []. */
    const normalizeSignUpDob = (value) => {
        if (value == null || value === '') return '';
        if (Array.isArray(value)) {
            if (value.length === 0) return '';
            const first = value[0];
            if (first instanceof Date && !Number.isNaN(first.getTime())) return first;
            return '';
        }
        if (value instanceof Date) {
            return Number.isNaN(value.getTime()) ? '' : value;
        }
        if (typeof value === 'string' && value.trim() === '') return '';
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? '' : parsed;
    };

    const signUpDobForPicker = (dob) => {
        const normalized = normalizeSignUpDob(dob);
        return normalized instanceof Date ? normalized : null;
    };

    const formatSignUpDobForApi = (dob) => {
        const normalized = normalizeSignUpDob(dob);
        if (!normalized || !(normalized instanceof Date)) return '';
        return normalized.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
        }).split('/').join('-');
    };

    // Validation functions
    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email) return { isValid: false, message: 'Email is required' };
        if (!emailRegex.test(email)) return { isValid: false, message: 'Email must be a valid email address' };
        return { isValid: true, message: '' };
    };

    const validatePassword = (password) => {
        if (!password) return { isValid: false, message: 'Password is required' };
        if (password.length < 8) return { isValid: false, message: 'Password must be at least 8 characters long' };
        if (/\s/.test(password)) return { isValid: false, message: 'Password cannot contain spaces' };
        if (
            !/[A-Z]/.test(password) ||
            !/[a-z]/.test(password) ||
            !/[0-9]/.test(password) ||
            !/[!@#$%^&*()_+{}\[\]:;<>,.?~\\/-]/.test(password)
        )
            return { isValid: false, message: 'Must have at least 1 number, 1 special character, 1 lowercase and 1 uppercase.' };
        return { isValid: true, message: '' };
    };

    const validateConfirmPassword = (password, confirmPassword) => {
        if (!confirmPassword) return { isValid: false, message: 'Confirm Password is required' };
        if (password !== confirmPassword) return { isValid: false, message: 'Password do not match' };
        return { isValid: true, message: '' };
    };

    const validateName = (name, fieldName) => {
        if (!name || name.trim() === '') return { isValid: false, message: `${fieldName} is required` };
        if (name.trim().length < 1) return { isValid: false, message: `${fieldName} must be at least 1 character long` };
        if (!/^[a-zA-Z\s]*$/.test(name)) return { isValid: false, message: `${fieldName} should only contain letters` };
        return { isValid: true, message: '' };
    };

    const validateGender = (gender) => {
        if (!gender) return { isValid: false, message: 'Please select your gender' };
        return { isValid: true, message: '' };
    };

    const validateDOB = (dob) => {
        if (!normalizeSignUpDob(dob)) return { isValid: true, message: '' };
        return { isValid: true, message: '' };
    };

    const validatePhone = (phone, requireComplete = false) => {
        if (!phone) return { isValid: true, message: '' };
        if (!/^\d+$/.test(phone)) return { isValid: false, message: 'Phone number must contain only digits' };
        if (phone.length > 10) return { isValid: false, message: 'Please enter 10 digits' };
        if (requireComplete && phone.length !== 10) return { isValid: false, message: 'Please enter 10 digits' };
        return { isValid: true, message: '' };
    };

    const validateRole = (role) => {
        if (!role) return { isValid: false, message: 'Please select a role' };
        return { isValid: true, message: '' };
    };

    // Check if we're on the signup route and set isSignUp accordingly
    // useEffect(() => {
    //     if (location.pathname === '/signup') {
    //         setIsSignUp(true);
    //     } else {
    //         setIsSignUp(false);
    //     }
    // }, [location.pathname]);

    // Fetch roles when sign up is selected
    useEffect(() => {
        if (isSignUp) {
            dispatch(UserTypesGet())
                .then((response) => {
                    setRoles(normalizeUserTypes(response.payload));
                })
                .catch((error) => {
                    console.error('Error fetching user types:', error);
                });

            dispatch(CountryCodesGet())
                .then((response) => {
                    console.log(response.payload, "response.payload");
                    const options = normalizeCountryCodeOptions(response.payload);
                    setCountryCodeOptions(options);
                    // set a safe default if missing/invalid
                    if (options.length > 0) {
                        setSignUpForm((prev) => {
                            const current = prev.phone_country_code;
                            const exists = options.some((o) => o.value === current);
                            return exists ? prev : { ...prev, phone_country_code: options[0].value };
                        });
                    }
                })
                .catch((error) => {
                    console.error('Error fetching country codes:', error);
                });
        }
    }, [isSignUp, dispatch]);

    useEffect(() => {
        const message = String(loginValidation.credentials?.message || '').trim();
        if (loginValidation.credentials?.isValid || !/passkey/i.test(message)) return;
        setLoginValidation((prev) => ({
            ...prev,
            credentials: { isValid: true, message: '' },
        }));
        showPmrError({ message, timer: 10000 });
    }, [loginValidation.credentials]);

    useEffect(() => {
        const email = String(loginForm.user_name || '').trim();
        const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        if (!passkeySupported || !emailOk) {
            setEmailHasPasskey(false);
            return undefined;
        }

        const cached = peekAccountHasPasskey(email);
        if (cached != null) {
            setEmailHasPasskey(cached);
            return undefined;
        }

        setEmailHasPasskey(false);
        let cancelled = false;
        const timer = setTimeout(() => {
            accountHasPasskey(email).then((hasPasskey) => {
                if (!cancelled) setEmailHasPasskey(Boolean(hasPasskey));
            });
        }, 400);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [loginForm.user_name, passkeySupported]);

    useEffect(() => {
        // First load of the site clears the saved session, same as before.
        // Back to "/" while the app is already open must not log the user out.
        if (window.__PMR_SPA_READY__ && hasPersistedSession(reduxLogin, reduxToken)) {
            const path = window.location.pathname || '/';
            if (path === '/' || path === '') {
                navigate(getResumeAppPath(reduxLogin, reduxToken) || '/landing-page', { replace: true });
            }
            return;
        }

        // Clear session storage and reset Redux
        sessionStorage.clear();
        dispatch({ type: 'LOGOUT' });

        // Clear specific localStorage items but preserve remembered email
        const rememberedCredentials = localStorage.getItem('rememberedCredentials');
        console.log('Login page load: Found remembered email:', rememberedCredentials ? 'Yes' : 'No');
        localStorage.clear();
        if (rememberedCredentials) {
            localStorage.setItem('rememberedCredentials', rememberedCredentials);

        }

        // Load remembered credentials if they exist
        // Note: This loads stored email from localStorage
        if (rememberedCredentials) {
            try {
                const credentials = JSON.parse(rememberedCredentials);

                setLoginForm({
                    user_name: credentials.user_name || '',
                    // Keep a browser-filled password. Remember Me stores the email only.
                    password: String(loginPasswordRef.current?.value || '').replace(/\s/g, '')
                });
                setRememberMe(true);
            } catch (error) {
                console.error('Error parsing remembered credentials:', error);
                localStorage.removeItem('rememberedCredentials');
            }
        } else {
            console.log('No remembered credentials found');
        }

        // Check if we're on a token route (reset/access/secure-login).
        // In some deployments the SPA may be entered via a hash URL (e.g. `/#/secure-login/<token>`).
        // Use a normalized "effective path" so route detection + token parsing is consistent.
        const hashPath = (window.location.hash || "").startsWith("#/")
            ? window.location.hash.slice(1) // "#/a/b" -> "/a/b"
            : "";
        const currentPath = hashPath || window.location.pathname;
        const urlParams = new URLSearchParams(window.location.search);
        const effectivePath = (currentPath || "/").toLowerCase();
        const isResetPassword = effectivePath.includes("/reset_password");
        const isOnetimeAccess = effectivePath.includes("/secure-login");
        const isAccessCode = effectivePath.includes("/access-code-validate");
        const isSignUp = effectivePath.includes("/signup");
        console.log(isSignUp, "isSignUp");


        // Check for token in path parameter (from useParams) or query parameters
        let resetToken = token || urlParams.get('reset_token') || urlParams.get('token');

        // Additional fallback: extract token from path if useParams didn't work
        //  let extractedToken = resetToken;
        if (!resetToken && (isResetPassword || isOnetimeAccess || isAccessCode)) {
            // If we landed on `/<route>/<token>`, prefer the last path segment.
            // This avoids cases where `useParams()` is empty due to router bootstrapping.
            const pathParts = (currentPath || '').split('/').filter(Boolean);
            const tokenFromPath = pathParts.length ? pathParts[pathParts.length - 1] : '';
            const lastSegmentLower = (tokenFromPath || '').toLowerCase();
            if (
                tokenFromPath &&
                lastSegmentLower !== 'reset_password' &&
                lastSegmentLower !== 'secure-login' &&
                lastSegmentLower !== 'access-code-validate'
            ) {
                resetToken = tokenFromPath;
            }
        }

        if (resetToken !== null && resetToken !== undefined && resetToken !== '' && isResetPassword) {
            if (isResetPassword) {
                console.log("Reset token found in URL path/query.");
                dispatch(validateResetPasswordToken({ URIToken: resetToken }))
                    .then((resp) => {

                        console.log(resp, "resp");

                        const resolvedUserId = resp?.payload?.user_id;
                        setUserIDforResetPassword(resolvedUserId);
                        if (resolvedUserId !== null && resolvedUserId !== undefined && resolvedUserId !== '' && resolvedUserId !== 0) {
                            setShowResetPassword(true);
                            setLoginForm(prev => ({
                                ...prev,
                                user_id: resolvedUserId,
                                reset_token: resetToken
                            }));
                            // Clean up URL for security
                            //window.history.replaceState({}, document.title, '/r/');
                            window.history.replaceState({}, document.title, '/');
                        } else {
                            setErrorMessage(resp.payload.errorMessage);
                            setShowErrorModal(true);
                            setShowResetPassword(false);
                            //window.history.replaceState({}, document.title, '/r/');
                            window.history.replaceState({}, document.title, '/');
                        }

                    })
                    .catch((error) => {
                        console.log(error, "error");
                    });
            }
        } else if (isOnetimeAccess) {
            dispatch(validateTokenAccessLink({ URIToken: resetToken }))
                .then((resp) => {

                    if (resp.payload.user_id !== null && resp.payload.user_id !== undefined && resp.payload.user_id !== '' && resp.payload.user_id !== 0) {
                        navigate('/landing-page', { replace: true })
                    } else {
                        setShowErrorReportForm(true)
                        setExpireMessage(resp.payload.errorMessage)
                        //window.history.replaceState({}, document.title, '/r/');
                        window.history.replaceState({}, document.title, '/');
                    }
                })
                .catch((error) => {
                    console.log(error, "error");
                });
        } else if (isAccessCode) {

            setShowAccessReports(true);
            setSharedReportsForm({
                share_code: resetToken,
                dob: '',
                email_address: '',
                usertype_id: '',
            });
            //window.history.replaceState({}, document.title, '/r/');
            window.history.replaceState({}, document.title, '/');

        } else {
            if (isSignUp) {
                setIsSignUp(true);
            } else {
                setIsSignUp(false);
            }

            window.history.replaceState({}, document.title, '/');
            // window.history.replaceState({}, document.title, '/pmr_qa/');    
        }

    }, [token]);

    const syncBrowserPasswordReminder = useCallback(() => {
        const email = String(loginEmailRef.current?.value || '').replace(/\s/g, '');
        const password = String(loginPasswordRef.current?.value || '').replace(/\s/g, '');
        if (!email && !password) return;
        setLoginForm((prev) => {
            const user_name = email || prev.user_name;
            const nextPassword = password || prev.password;
            if (user_name === prev.user_name && nextPassword === prev.password) return prev;
            return { user_name, password: nextPassword };
        });
    }, []);

    useEffect(() => {
        const timers = [150, 500, 1200].map((ms) => setTimeout(syncBrowserPasswordReminder, ms));
        return () => timers.forEach(clearTimeout);
    }, [syncBrowserPasswordReminder]);

    const handleLoginAutofillAnimation = (e) => {
        if (e.animationName === 'login-autofill-start') syncBrowserPasswordReminder();
    };

    const handleLoginChange = (e) => {
        const { name, value } = e.target;
        setLoginForm((prev) => ({ ...prev, [name]: value }));

        // Real-time validation when user types
        if (loginSubmitted) {
            if (name === 'user_name') {
                setLoginValidation(prev => ({
                    ...prev,
                    email: validateLoginEmail(value)
                }));
            } else if (name === 'password') {
                setLoginValidation(prev => ({
                    ...prev,
                    password: validateLoginPassword(value)
                }));
            }
        }

        // Clear email validation error when user starts typing (for reset password scenario)
        if (name === 'user_name') {
            if (value.trim() !== '') {
                setLoginValidation(prev => ({
                    ...prev,
                    email: { isValid: true, message: '' }
                }));
            }
        }
    };

    const handleSignUpChange = (e) => {
        const { name, value } = e.target;

        if (name === 'phone_country_code') {
            setSignUpForm(prev => ({ ...prev, phone_country_code: value }));
            return;
        }

        if (name === 'phone_number') {
            const numericValue = value.replace(/[^0-9]/g, '').slice(0, 10);
            setSignUpForm(prev => ({ ...prev, phone_number: numericValue }));
            setSignUpValidation(prev => ({
                ...prev,
                phone_number: validatePhone(numericValue)
            }));
            return;
        }

        if (name === 'dob') {
            const normalizedDob = normalizeSignUpDob(value);
            setSignUpForm(prev => ({ ...prev, dob: normalizedDob }));
            setSignUpValidation(prev => ({
                ...prev,
                dob: validateDOB(normalizedDob)
            }));
            return;
        }

        setSignUpForm(prev => ({ ...prev, [name]: value }));

        // Real-time validation for all fields
        if (name === 'firstName') {
            setSignUpValidation(prev => ({
                ...prev,
                firstName: validateName(value.trim(), 'First name')
            }));
        } else if (name === 'lastName') {
            setSignUpValidation(prev => ({
                ...prev,
                lastName: validateName(value.trim(), 'Last name')
            }));
        } else if (name === 'gender') {
            setSignUpValidation(prev => ({
                ...prev,
                gender: validateGender(value)
            }));
        } else if (name === 'role' || name === 'usertype_id') {
            setSignUpValidation(prev => ({
                ...prev,
                role: validateRole(value)
            }));
        } else if (name === 'email') {
            // Clear any server-side validation errors when user starts typing
            setSignUpValidation(prev => ({
                ...prev,
                email: validateEmail(value)
            }));
        } else if (name === 'password') {
            setSignUpValidation(prev => ({
                ...prev,
                password: validatePassword(value),
                confirmPassword: validateConfirmPassword(value, signUpForm.confirmPassword)
            }));
        } else if (name === 'confirmPassword') {
            setSignUpValidation(prev => ({
                ...prev,
                confirmPassword: validateConfirmPassword(signUpForm.password, value)
            }));
        }
    };

    const clearForms = () => {

        setSignUpForm({
            firstName: '',
            lastName: '',
            gender: '',
            phone_number: '',
            phone_country_code: '+1',
            dob: '',
            email: '',
            password: '',
            confirmPassword: '',
            role: 'User',
            usertype_id: '2'
        });
        setSignUpSubmitted(false);
        // Reset sign-up validation state
        resetSignUpValidation();
        setShowResetPassword(false);
        setResetPasswordForm({ newPassword: '', confirmPassword: '' });
        setResetPasswordSubmitted(false);
        setShowNewPassword(false);
        setShowConfirmNewPassword(false);
        setShowOneTimeAccessForm(false);
        setOneTimeAccessForm({ email: '' });
        setOneTimeAccessSubmitted(false);
        setOneTimeAccessValidation({ email: { isValid: true, message: '' } });
        setShowErrorReportForm(false);
    };

    const clearLoginForm = () => {
        setLoginForm({ user_name: '', password: '' });
        setRememberMe(false);
        localStorage.removeItem('rememberedCredentials');
    };

    const completeAuthenticatedSession = (payload) => {
        localStorage.removeItem('announcementDismissed');
        localStorage.setItem('isFirstTimeLogin', 'true');

        if (payload.user_type_name === "Provider") {
            navigate('/landing-page', { replace: true });
        } else if (payload.user_type_name === "Admin") {
            navigate('/admin-landing-page', { replace: true, state: { data: payload } });
        } else {
            navigate('/landing-page', { replace: true, state: { data: payload } });
        }
    };

    const pickLoginUserId = (payload) => pickPasskeyUserId(payload);

    const restoreLoginSession = (payload) => {
        const session = normalizeLoginSession(payload) || payload;
        if (!session?.Token && !session?.token) return;
        const menuItems = session.menu_items || session.menuItems || session.Table1 || [];
        const familyAccounts = session.family_accounts || session.familyAccounts || session.Table2 || [];
        dispatch(setLoginDetails(session));
        dispatch(setMenuItem(Array.isArray(menuItems) ? menuItems : []));
        dispatch(setFamilyAccounts(Array.isArray(familyAccounts) ? familyAccounts : []));
        dispatch(setToken(session.Token || session.token));
    };

    const enterAuthenticatedApp = (payload) => {
        restoreLoginSession(payload);
        setShowPasskeySetupModal(false);
        setPendingLoginSession(null);
        setPendingSignupCredentials(null);
        setPasskeyError('');
        completeAuthenticatedSession(payload);
    };

    const offerPasskeyAfterPasswordLogin = async (payload) => {
        const session = normalizeLoginSession(payload) || payload;
        if (!pickPasskeyToken(session)) {
            setLoginValidation((prev) => ({
                ...prev,
                credentials: { isValid: false, message: 'Invalid Credentials' }
            }));
            setIsLoading(false);
            return;
        }

        const token = pickPasskeyToken(session);
        const userId = pickPasskeyUserId(session);
        const userName = session.user_name || session.email_address || loginForm.user_name;
        try {
            const rows = await listPasskeys({
                token,
                userId,
                userName,
                emailAddress: userName,
            });
            // Only offer create when the API confirms this account has zero passkeys.
            // Never offer on list failure — that wrongly creates a new passkey on other devices.
            if (Array.isArray(rows) && rows.length === 0) {
                markPasskeyOfferAfterLogin();
            }
        } catch {
            // Keep going without create offer if we cannot verify passkey list.
        }

        enterAuthenticatedApp(session);
    };

    const persistRememberedEmail = (email) => {
        const user_name = email ?? loginForm.user_name;
        if (rememberMe) {
            localStorage.setItem('rememberedCredentials', JSON.stringify({
                user_name
            }));
        } else {
            localStorage.removeItem('rememberedCredentials');
        }
    };

    const handleLoginSubmit = (e) => {
        e.preventDefault();
        const user_name = String(loginEmailRef.current?.value || loginForm.user_name || '').replace(/\s/g, '');
        const password = String(loginPasswordRef.current?.value || loginForm.password || '').replace(/\s/g, '');
        setLoginForm({ user_name, password });
        setLoginSubmitted(true);
        setIsLoading(true); // Start loading

        // Validate all fields
        const newValidation = {
            email: validateLoginEmail(user_name),
            password: validateLoginPassword(password),
            credentials: { isValid: true, message: '' }
        };

        setLoginValidation(newValidation);

        // Check if any validation failed
        const hasErrors = Object.values(newValidation).some(field => !field.isValid);
        if (hasErrors) {
            setIsLoading(false);
            return;
        }

        persistRememberedEmail(user_name);

        dispatch(loginUser({ user_name, password }))
            .then((resp) => {
                if (resp?.meta?.requestStatus === 'rejected') {
                    setLoginValidation((prev) => ({
                        ...prev,
                        credentials: { isValid: false, message: 'An error occurred during login. Please try again.' }
                    }));
                    setIsLoading(false);
                    return;
                }
                const loginPayload = normalizeLoginSession(resp.payload) || resp.payload;
                const loginToken = pickPasskeyToken(loginPayload);
                if (loginToken) {
                    offerPasskeyAfterPasswordLogin(loginPayload);
                    return;
                }
                setLoginValidation((prev) => ({
                    ...prev,
                    credentials: { isValid: false, message: 'Invalid Credentials' }
                }));
                setIsLoading(false);
            })
            .catch(() => {
                setLoginValidation((prev) => ({
                    ...prev,
                    credentials: { isValid: false, message: 'An error occurred during login. Please try again.' }
                }));
                setIsLoading(false);
            });
    };

    const passkeyFailureText = (error, fallback) => {
        if (!error) return fallback;
        if (typeof error === 'string' && error.trim()) return error.trim();
        const direct = error.message || error.ErrorMessage || error.errorMessage;
        if (typeof direct === 'string' && direct.trim()) return direct.trim();
        return getPasskeyApiError(error, fallback);
    };

    const showPasskeyLoginPopup = (message) => {
        const text = (message || '').trim() || 'Could not sign in with passkey. Please try again.';
        setLoginValidation(prev => ({
            ...prev,
            credentials: { isValid: true, message: '' }
        }));
        showPmrError({ message: text, timer: 2000 });
    };

    const handlePasskeyLogin = async () => {
        const emailCheck = validateLoginEmail(loginForm.user_name);
        setLoginSubmitted(true);
        setLoginValidation({
            email: emailCheck,
            password: { isValid: true, message: '' },
            credentials: { isValid: true, message: '' }
        });
        if (!emailCheck.isValid) return;

        persistRememberedEmail();
        setIsLoading(true);
        let signedIn = false;
        try {
            const data = await dispatch(passkeyLogin({ user_name: loginForm.user_name })).unwrap();
            if (data?.Token) {
                clearPasskeyOfferAfterLogin();
                signedIn = true;
                completeAuthenticatedSession(data);
                return;
            }
            showPasskeyLoginPopup(
                data?.ErrorMessage || data?.errorMessage || data?.message || 'Could not sign in with passkey. Please try again.'
            );
        } catch (error) {
            if (isPasskeyCancelled(error)) return;
            showPasskeyLoginPopup(
                passkeyFailureText(error, 'Could not sign in with passkey. Sign in with your password instead.')
            );
        } finally {
            if (!signedIn) setIsLoading(false);
        }
    };

    const handleSignUpSubmit = (e) => {
        e.preventDefault();
        setSignUpSubmitted(true);

        // Validate all fields
        const newValidation = {
            firstName: validateName(signUpForm.firstName, 'First name'),
            lastName: validateName(signUpForm.lastName, 'Last name'),
            gender: validateGender(signUpForm.gender),
            phone_number: validatePhone(signUpForm.phone_number, true),
            dob: validateDOB(signUpForm.dob),
            email: validateEmail(signUpForm.email),
            password: validatePassword(signUpForm.password),
            confirmPassword: validateConfirmPassword(signUpForm.password, signUpForm.confirmPassword),
            role: validateRole(signUpForm.role)
        };

        setSignUpValidation(newValidation);

        // Check if any validation failed
        const hasErrors = Object.values(newValidation).some(field => !field.isValid);
        if (hasErrors) {
            return;
        }

        setIsLoading(true);

        const formDataToSubmit = {
            ...signUpForm,
            dob: formatSignUpDobForApi(signUpForm.dob),
            phone_number: signUpForm.phone_number
                ? `${signUpForm.phone_country_code}${signUpForm.phone_number}`
                : '',
        };

        // If all validations pass, proceed with signup
        dispatch(userSignUp(formDataToSubmit))
            .then((resp) => {
                if (resp?.meta?.requestStatus === 'rejected') {
                    return;
                }
                const raw = resp?.payload;
                const data = raw && typeof raw === 'object' && ('SuccessMessage' in raw || 'ErrorMessage' in raw)
                    ? raw
                    : raw?.data;
                if (!data) {
                    return;
                }
                if (data.SuccessMessage) {
                    setPendingSignupCredentials({
                        user_name: signUpForm.email,
                        password: signUpForm.password,
                    });
                    setSuccessMessage(data.SuccessMessage);
                    setShowSuccessModal(true);
                    localStorage.removeItem('rememberedCredentials');
                    setRememberMe(false);
                    setSignUpForm({
                        firstName: '',
                        lastName: '',
                        gender: '',
                        phone_number: '',
                        phone_country_code: '+1',
                        dob: '',
                        email: '',
                        password: '',
                        confirmPassword: '',
                        role: 'User',
                        usertype_id: '2'
                    });
                    setSignUpSubmitted(false);
                    resetSignUpValidation();
                } else if (data.ErrorMessage === "Email address already exists.") {
                    // Update email validation to show error directly on the form
                    setSignUpValidation(prev => ({
                        ...prev,
                        email: { isValid: false, message: data.ErrorMessage }
                    }));
                } else if (data.ErrorMessage) {
                    setErrorMessage(data.ErrorMessage);
                    setShowErrorModal(true);
                }
            })
            .catch(() => {
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const finishSignupWithoutPasskey = () => {
        setShowPasskeySetupModal(false);
        setPendingSignupCredentials(null);
        setPasskeyError('');
        setIsSignUp(false);
        navigate('/');
    };

    const handlePasskeySetupSkip = () => {
        if (pendingLoginSession) {
            enterAuthenticatedApp(pendingLoginSession);
            return;
        }
        if (passkeyBusy || isLoading) return;
        finishSignupWithoutPasskey();
    };

    const handlePasskeySetupConfirm = async () => {
        const session = normalizeLoginSession(pendingLoginSession) || pendingLoginSession;
        const sessionToken =
            pickPasskeyToken(session) ||
            pickPasskeyToken(reduxLogin) ||
            (typeof reduxToken === 'string' ? reduxToken.trim() : '');
        const sessionUserId = pickLoginUserId(session) || pickLoginUserId(reduxLogin);
        if (sessionToken) {
            setPasskeyError('');
            setIsLoading(true);
            try {
                await registerPasskeyOnDevice({
                    userName: loginForm.user_name || session?.user_name || session?.email_address,
                    emailAddress: loginForm.user_name || session?.email_address || session?.user_name,
                    userId: sessionUserId,
                    token: sessionToken,
                    deviceName: getDeviceLabel(),
                });
            } catch (err) {
                if (isPasskeyCancelled(err)) {
                    setIsLoading(false);
                    return;
                }
                setPasskeyError(getPasskeyApiError(err, 'Passkey could not be added. You can continue without it, or try again.'));
                setIsLoading(false);
                return;
            }
            setShowPasskeySetupModal(false);
            setIsLoading(false);
            await showPmrSuccess({
                title: 'Passkey created!',
                message: 'You can now sign in with your fingerprint, face, or screen lock.',
                timer: 2500,
            });
            enterAuthenticatedApp(session);
            return;
        }

        if (!pendingSignupCredentials?.user_name || !pendingSignupCredentials?.password) {
            finishSignupWithoutPasskey();
            return;
        }
        setPasskeyError('');
        setIsLoading(true);
        try {
            const loginData = normalizeLoginSession(await dispatch(loginUser(pendingSignupCredentials)).unwrap()) || {};
            if (!pickPasskeyToken(loginData)) {
                setPasskeyError('Account created. Please sign in with your password to add a passkey.');
                return;
            }
            let passkeyCreated = false;
            try {
                await registerPasskeyOnDevice({
                    userName: pendingSignupCredentials.user_name,
                    emailAddress: pendingSignupCredentials.user_name,
                    userId: pickLoginUserId(loginData),
                    token: pickPasskeyToken(loginData),
                    deviceName: getDeviceLabel(),
                });
                passkeyCreated = true;
            } catch (err) {
                if (!isPasskeyCancelled(err)) {
                    setPasskeyError(getPasskeyApiError(err, 'Passkey could not be added. You can continue with your password.'));
                }
            }
            setShowPasskeySetupModal(false);
            setPendingSignupCredentials(null);
            if (passkeyCreated) {
                await showPmrSuccess({
                    title: 'Passkey created!',
                    message: 'You can now sign in with your fingerprint, face, or screen lock.',
                    timer: 2500,
                });
            }
            completeAuthenticatedSession(loginData);
        } catch (error) {
            setPasskeyError(getPasskeyApiError(error, 'Account created. Please sign in with your password.'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleModalClose = () => {
        setShowSuccessModal(false);
        setShowWarningModal(false);
        setWarningMessage('');
        setShowResetPassword(false);
        setShowAccessReports(false);
        setShowOneTimeAccessForm(false);
        setShowErrorReportForm(false);
        localStorage.removeItem('rememberedCredentials');
        setResetPasswordForm({ newPassword: '', confirmPassword: '' });
        setResetPasswordSubmitted(false);
        setShowNewPassword(false);
        setShowConfirmNewPassword(false);
        setOneTimeAccessForm({ email: '' });
        setOneTimeAccessSubmitted(false);
        setOneTimeAccessValidation({ email: { isValid: true, message: '' } });
        setIsLoading(false);
        if (pendingSignupCredentials) {
            setPendingSignupCredentials(null);
            setPasskeyError('');
            setShowPasskeySetupModal(false);
            setIsSignUp(false);
            return;
        }
        setIsSignUp(false);
    };

    const handleAccessReport = () => {
        setShowAccessReports(true)
        setIsSignUp(false);
    }

    const handleSharedReportsChange = (e) => {
        const { name, value } = e.target;

        if (name === "dob") {

            const formattedDate = value ? new Date(value).toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric'
            }).split('/').join('-') : '';

            setSharedReportsForm(prev => ({
                ...prev,
                dob: formattedDate
            }));
        } else {
            setSharedReportsForm(prev => ({
                ...prev,
                [name]: value
            }));
        }
        setSharedReportsForm(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear validation error when user makes changes
        setValidationError('');
    };

    // In Login.js, modify the handleViewSharedReports function:
    const handleViewSharedReports = (e) => {
        e.preventDefault();
        setIsLoading(true);
        setValidationError('');

        // Validate required fields
        if (!sharedReportsForm.share_code) {
            setValidationError('Access code is required');
            setIsLoading(false);
            return;
        }

        if (!sharedReportsForm.dob && !sharedReportsForm.email_address) {
            setValidationError('Please provide either date of birth or email address');
            setIsLoading(false);
            return;
        }

        const formattedDate = sharedReportsForm.dob ? new Date(sharedReportsForm.dob).toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
        }).split('/').join('/') : '';
        const formData = {
            date_of_birth: formattedDate,
            email_address: sharedReportsForm.email_address,
            share_code: sharedReportsForm.share_code,
        }


        dispatch(sharedReportsValidateCode(formData))
            .then((resp) => {
                if (resp.payload && resp.payload.Token) {
                    // Store the response in Redux
                    dispatch({
                        type: 'SET_LOGIN_DETAILS',
                        payload: resp.payload
                    });
                    if (resp.payload.role === "User") {
                        navigate('/landing-page', { replace: true });
                    }
                    // Navigate with the response data
                    // navigate('/access-landing-page', {
                    //     state: {
                    //         data: resp.payload,
                    //         accessType: 'shared'
                    //     }
                    // });
                } else {
                    setValidationError('Invalid share code or user details does not match.');
                }
            })
            .catch((error) => {
                setValidationError(error.message || 'Failed to validate access code. Please try again.');
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleEmailAction = (action) => {
        setIsLoading(true);
        if (!loginForm.user_name) {
            setLoginValidation(prev => ({
                ...prev,
                email: { isValid: false, message: 'Please enter your email address first' },
                password: { isValid: true, message: '' }
            }));
            setIsLoading(false);
            return;
        }

        // Validate email format for reset password and one-time access actions
        if (action === 'reset' || action === 'access') {
            const emailValidation = validateLoginEmail(loginForm.user_name);
            if (!emailValidation.isValid) {
                setLoginValidation(prev => ({
                    ...prev,
                    email: emailValidation
                }));
                setIsLoading(false);
                return;
            }
        }

        dispatch(validateUserEmail({ email: loginForm.user_name }))
            .then((resp) => {
                if (resp.payload && resp.payload.UserID > 0) {
                    if (action === 'access') {
                        dispatch(generateOneTimeLoginLink({ user_id: resp.payload.UserID }))
                            .then((resp) => {
                                if (resp.payload) {
                                    setSuccessMessage("An email with a one-time access link has been sent to your registered email address. Please check your inbox.");
                                    setShowSuccessModal(true);
                                    setIsLoading(false); // Stop loading after showing success modal
                                } else {
                                    setErrorMessage(resp.payload.ErrorMessage);
                                    setShowErrorModal(true);
                                    setIsLoading(false); // Stop loading after showing error modal
                                }
                            })
                            .catch((error) => {
                                setErrorMessage(error.response?.data?.ErrorMessage);
                                setShowErrorModal(true);
                                setIsLoading(false); // Stop loading after showing error modal
                            });
                    } else if (action === 'reset') {
                        const resetUserId = resp.payload.UserID;
                        dispatch(resetPasswordEmailNotification({ user_id: resetUserId }))
                            .then((resp) => {
                                if (resp.payload) {
                                    const successText = "An email with instructions to reset your password has been sent to your registered email address. Please check your inbox.";
                                    const notice = resp.payload && typeof resp.payload === 'object' ? resp.payload : null;
                                    setSuccessMessage(successText);
                                    setShowSuccessModal(true);
                                    handleEmailModalClose();
                                    setIsLoading(false);
                                    showNotificationIfAllowed({
                                        userId: resetUserId,
                                        token: reduxToken || reduxLogin?.Token,
                                        title: notice?.title || "Password reset link sent successfully",
                                        body: notice?.message || successText,
                                        url: notice?.url || `${window.location.origin}/`,
                                        tag: `pmr-reset-link-${Date.now()}`,
                                    }).catch(() => {});
                                } else {
                                    setErrorMessage(resp.payload.ErrorMessage);
                                    setShowErrorModal(true);
                                    setIsLoading(false); // Stop loading after showing error modal
                                }
                            })
                            .catch((error) => {
                                setErrorMessage(error.response?.data?.ErrorMessage);
                                setShowErrorModal(true);
                                setIsLoading(false); // Stop loading after showing error modal
                            });
                    }
                } else {
                    setErrorMessage(resp.payload.ErrorMessage);
                    setShowErrorModal(true);
                    setIsLoading(false); // Stop loading after showing error modal
                }
            })
            .catch((error) => {
                setErrorMessage(error.response?.data?.ErrorMessage);
                setShowErrorModal(true);
                setIsLoading(false); // Stop loading after showing error modal
            });
    };

    const handleEmailModalClose = () => {
        setShowEmailModal(false);
        setEmailForm({ email: '' });
        setEmailError('');
        setEmailAction('');
        setIsLoading(false); // Ensure loading is stopped when modal is closed
    };

    const validateLoginEmail = (email) => {
        if (!email) return { isValid: false, message: 'Email is required' };
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) return { isValid: false, message: 'Please enter a valid email address' };
        return { isValid: true, message: '' };
    };

    const validateLoginPassword = (password) => {
        if (!password) return { isValid: false, message: 'Password is required' };
        return { isValid: true, message: '' };
    };

    const handleResetPasswordChange = (e) => {
        const { name, value } = e.target;
        setResetPasswordForm(prev => ({ ...prev, [name]: value }));

        // Real-time validation for password fields
        if (name === 'newPassword') {
            setResetPasswordValidation(prev => ({
                ...prev,
                newPassword: validatePassword(value),
                confirmPassword: validateConfirmPassword(value, resetPasswordForm.confirmPassword)
            }));
        } else if (name === 'confirmPassword') {
            setResetPasswordValidation(prev => ({
                ...prev,
                confirmPassword: validateConfirmPassword(resetPasswordForm.newPassword, value)
            }));
        }
    };

    const handleResetPasswordSubmit = (e) => {
        e.preventDefault();
        setResetPasswordSubmitted(true);
        setIsLoading(true);

        // Validate all fields
        const newValidation = {
            newPassword: validatePassword(resetPasswordForm.newPassword),
            confirmPassword: validateConfirmPassword(resetPasswordForm.newPassword, resetPasswordForm.confirmPassword)
        };

        setResetPasswordValidation(newValidation);

        // Check if any validation failed
        const hasErrors = Object.values(newValidation).some(field => !field.isValid);
        if (hasErrors) {
            setIsLoading(false);
            return;
        }

        const params = {
            user_id: userIDforResetPassword,
            password: resetPasswordForm.newPassword
        };
        dispatch(resetUserPassword(params))
            .then((resp) => {
                const data = resp?.payload?.data ?? resp?.payload;
                const apiMessage = (
                    (typeof data === 'string' ? data : null)
                    ?? data?.error_message
                    ?? data?.errorMessage
                    ?? ''
                );
                const normalizedMessage = String(apiMessage).trim();
                const isPasswordResetSuccess = /successfully/i.test(normalizedMessage);

                if (isPasswordResetSuccess) {
                    const notice = data && typeof data === 'object' ? data : null;
                    setSuccessMessage(normalizedMessage);
                    setShowSuccessModal(true);
                    setIsLoading(false);
                    showNotificationIfAllowed({
                        userId: userIDforResetPassword,
                        token: reduxToken || reduxLogin?.Token,
                        title: notice?.title || 'Password reset successfully',
                        body: notice?.message || normalizedMessage,
                        url: notice?.url || `${window.location.origin}/`,
                        tag: `pmr-password-reset-${Date.now()}`,
                    }).catch(() => {});
                    return;
                }

                setErrorMessage(normalizedMessage);
                setShowErrorModal(true);
                setIsLoading(false);
            })
            .catch((error) => {
                const errorData = error.response?.data;
                setErrorMessage(
                    (typeof errorData === 'string' ? errorData : null)
                    ?? errorData?.error_message
                    ?? errorData?.errorMessage
                    ?? errorData?.ErrorMessage
                    ?? ''
                );
                setShowErrorModal(true);
                setIsLoading(false);
            });

    };

    const handleResetPasswordClose = () => {
        setShowResetPassword(false);
        setResetPasswordForm({ newPassword: '', confirmPassword: '' });
        setResetPasswordValidation({
            newPassword: { isValid: true, message: '' },
            confirmPassword: { isValid: true, message: '' }
        });
        setResetPasswordSubmitted(false);
        setShowNewPassword(false);
        setShowConfirmNewPassword(false);

        // Navigate back to login page
        if (window.location.pathname.includes('/reset_password')) {
            //navigate('/r');
            navigate('/');
        }
    };

    const handleOneTimeAccessChange = (e) => {
        const { name, value } = e.target;
        setOneTimeAccessForm(prev => ({ ...prev, [name]: value }));

        // Real-time validation when user types
        if (oneTimeAccessSubmitted) {
            if (name === 'email') {
                setOneTimeAccessValidation(prev => ({
                    ...prev,
                    email: validateEmail(value)
                }));
            }
        }
    };

    const handleOneTimeAccessSubmit = (e) => {
        e.preventDefault();
        setOneTimeAccessSubmitted(true);
        setIsLoading(true);

        // Validate email
        const emailValidation = validateEmail(oneTimeAccessForm.email);
        setOneTimeAccessValidation({ email: emailValidation });

        if (!emailValidation.isValid) {
            setIsLoading(false);
            return;
        }

        // Call the existing email action for access
        dispatch(validateUserEmail({ email: oneTimeAccessForm.email }))
            .then((resp) => {
                if (resp.payload && resp.payload.UserID > 0) {
                    dispatch(generateOneTimeLoginLink({ user_id: resp.payload.UserID }))
                        .then((resp) => {
                            if (resp.payload) {
                                setSuccessMessage("An email with a one-time access link has been sent to your registered email address. Please check your inbox.");
                                setShowSuccessModal(true);
                                setShowOneTimeAccessForm(false);
                                setOneTimeAccessForm({ email: '' });
                                setOneTimeAccessSubmitted(false);
                                setIsLoading(false);
                            } else {
                                setErrorMessage(resp.payload?.ErrorMessage || "Failed to send access link");
                                setShowErrorModal(true);
                                setIsLoading(false);
                            }
                        })
                        .catch((error) => {
                            setErrorMessage(error.response?.data?.ErrorMessage || "Failed to send access link");
                            setShowErrorModal(true);
                            setIsLoading(false);
                        });
                } else {
                    setErrorMessage(resp.payload?.ErrorMessage || "Email address not found");
                    setShowErrorModal(true);
                    setIsLoading(false);
                }
            })
            .catch((error) => {
                setErrorMessage(error.response?.data?.ErrorMessage || "Failed to validate email");
                setShowErrorModal(true);
                setIsLoading(false);
            });
    };

    const showEmailPasskeyIcon = passkeySupported && emailHasPasskey;
    const resumePath = isLoading ? '' : getResumeAppPath(reduxLogin, reduxToken);
    if (resumePath) {
        return <Navigate to={resumePath} replace />;
    }

    return (
        <div className="login-page">
            <div className="login-header">
                <img src={acmeHealthLogo} alt="ACME Health" />
            </div>
            {isLoading && (
                <div className="login-loading-overlay">
                    <LoadingSpinner  />
                </div>
            )}
            <Container fluid className="d-flex bg-black align-items-center justify-content-center login-container">
                <Row className="w-100 h-100 position-relative m-0">
                    <div className="login-background">
                        <img
                            src={loginLanding}
                            alt="Doctor"
                        />
                    </div>

                    <Col
                        xs={12}
                        md={isSignUp ? 6 : 5}
                        lg={isSignUp ? 3 : 5}
                        className="position-absolute bg-white p-3 rounded shadow-lg login-form-container"
                        style={{
                            width: (showAccessReports || showOneTimeAccessForm || showErrorReportForm) ? '530px' : undefined,
                            overflow: 'visible',
                        }}
                    >
                        {isSignUp ? (
                            <div className="signup-container">
                                <h3 className="mb-3 fs-8 fw-bold" style={{ color: '#5e5873' }}>Welcome to Acme Health! <span role="img" aria-label="wave">👋</span></h3>
                                <p className="fw mb-3 small" style={{ color: '#5e5873' }}>Please register to create an account</p>
                                <Form onSubmit={handleSignUpSubmit} className="signup-form">
                                    <Form.Group className="mb-2">
                                        <Form.Label className="mb-0 fw-bold small" style={{ color: '#5e5873' }}>First Name</Form.Label>
                                        {/* <Form.Control
                                            type="text"
                                            name="firstName"
                                            value={signUpForm.firstName}
                                            onChange={handleSignUpChange}
                                            size="sm"
                                        /> */}
                                        <div className="position-relative">
                                            <Form.Control
                                                type="text"
                                                name="firstName"
                                                value={signUpForm.firstName}
                                                onChange={handleSignUpChange}
                                                size="sm"
                                                className={(!signUpValidation.firstName.isValid && signUpSubmitted) ? 'is-invalid' : ''}
                                            />
                                            {(!signUpValidation.firstName.isValid && signUpSubmitted) && (
                                                <span className="position-absolute end-0 top-50 translate-middle-y pe-2 text-danger">
                                                    <i className="bi bi-exclamation-circle"></i>
                                                </span>
                                            )}
                                        </div>
                                        {(!signUpValidation.firstName.isValid && signUpSubmitted) && (
                                            <div className="invalid-feedback d-block fw-bold">{signUpValidation.firstName.message}</div>
                                        )}
                                    </Form.Group>

                                    <Form.Group className="mb-2">
                                        <Form.Label className="mb-0 fw-bold small" style={{ color: '#5e5873' }}>Last Name</Form.Label>
                                        <div className="position-relative">
                                            <Form.Control
                                                type="text"
                                                name="lastName"
                                                value={signUpForm.lastName}
                                                onChange={handleSignUpChange}
                                                size="sm"
                                                className={(!signUpValidation.lastName.isValid && signUpSubmitted) ? 'is-invalid' : ''}
                                            />
                                            {(!signUpValidation.lastName.isValid && signUpSubmitted) && (
                                                <span className="position-absolute end-0 top-50 translate-middle-y pe-2 text-danger">
                                                    <i className="bi bi-exclamation-circle"></i>
                                                </span>
                                            )}
                                        </div>
                                        {(!signUpValidation.lastName.isValid && signUpSubmitted) && (
                                            <div className="invalid-feedback d-block fw-bold">{signUpValidation.lastName.message}</div>
                                        )}

                                    </Form.Group>

                                    <Form.Group className="mb-2">
                                        <Form.Label className="mb-0 fw-bold small" style={{ color: '#5e5873' }}>Date of Birth</Form.Label>
                                        <div className="position-relative">
                                            <Flatpickr
                                                value={signUpDobForPicker(signUpForm.dob) ?? ''}
                                                onChange={(selectedDates) => handleSignUpChange({ target: { name: 'dob', value: selectedDates } })}
                                                dateFormat="MM/dd/yyyy"
                                                placeholder="MM/DD/YYYY"
                                                className={`form-control bg-white border ${(!signUpValidation.dob.isValid && signUpSubmitted) ? 'is-invalid' : ''}`}
                                                name="dob"
                                                showMonthDropdown
                                                showYearDropdown
                                                dropdownMode="select"
                                                autoComplete="off"
                                                options={{
                                                    dateFormat: "m/d/Y",
                                                    maxDate: new Date(),
                                                    allowInput: true,
                                                    clickOpens: true,
                                                    theme: "material_green"
                                                }}
                                            />
                                            {(!signUpValidation.dob.isValid && signUpSubmitted) && (
                                                <span className="position-absolute end-0 top-50 translate-middle-y pe-2 text-danger">
                                                    <i className="bi bi-exclamation-circle"></i>
                                                </span>
                                            )}
                                        </div>
                                        {(!signUpValidation.dob.isValid && signUpSubmitted) && (
                                            <div className="invalid-feedback d-block fw-bold">{signUpValidation.dob.message}</div>
                                        )}
                                    </Form.Group>

                                    <Row className="mb-2 g-2 signup-gender-phone-row">
                                        <Col xs={6} className="signup-gender-col">
                                            <Form.Group>
                                                <Form.Label className="mb-0 fw-bold small" style={{ color: '#5e5873' }}>Gender</Form.Label>
                                                <div className="signup-select-wrapper">
                                                    <PmrSelect
                                                        name="gender"
                                                        value={signUpForm.gender}
                                                        onChange={(value) => handleSignUpChange({ target: { name: 'gender', value } })}
                                                        options={SIGNUP_GENDER_OPTIONS}
                                                        placeholder="Select your gender"
                                                        isInvalid={!signUpValidation.gender.isValid && signUpSubmitted}
                                                        compact
                                                    />
                                                </div>
                                                {(!signUpValidation.gender.isValid && signUpSubmitted) && (
                                                    <div className="invalid-feedback d-block fw-bold">{signUpValidation.gender.message}</div>
                                                )}
                                            </Form.Group>
                                        </Col>
                                        <Col xs={6} className="signup-phone-col">
                                            <Form.Group>
                                                <Form.Label className="mb-0 fw-bold small" style={{ color: '#5e5873' }}>Phone Number</Form.Label>
                                                <div className="position-relative">
                                                    <div
                                                        className={`signup-phone-combined signup-inline-field ${(!signUpValidation.phone_number.isValid && signUpSubmitted) ? 'is-invalid' : ''}`}
                                                    >
                                                        <PmrSelect
                                                            name="phone_country_code"
                                                            value={signUpForm.phone_country_code}
                                                            onChange={(value) => handleSignUpChange({ target: { name: 'phone_country_code', value } })}
                                                            options={countryCodeOptions ?? []}
                                                            placeholder="+1"
                                                            embedded
                                                            compact
                                                            isSearchable
                                                            aria-label="Country code"
                                                        />
                                                        <Form.Control
                                                            type="text"
                                                            name="phone_number"
                                                            value={signUpForm.phone_number}
                                                            onChange={handleSignUpChange}
                                                            onKeyDown={(e) => {
                                                                if (e.key === ' ') e.preventDefault();
                                                            }}
                                                            inputMode="numeric"
                                                            maxLength={10}
                                                            autoComplete="tel-national"
                                                            className="signup-phone-number-input"
                                                        />
                                                    </div>
                                                  
                                                    {(!signUpValidation.phone_number.isValid && signUpSubmitted) && (
                                                        <span className="signup-field-validation-icon text-danger">
                                                            <i className="bi bi-exclamation-circle"></i>
                                                        </span>
                                                    )}
                                                </div>
                                                {(!signUpValidation.phone_number.isValid && signUpSubmitted) && (
                                                    <div className="invalid-feedback d-block fw-bold">{signUpValidation.phone_number.message}</div>
                                                )}
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Form.Group className="mb-2">
                                        <Form.Label className="mb-0 fw-bold small" style={{ color: '#5e5873' }}>Email</Form.Label>
                                        <div className="position-relative">
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                value={signUpForm.email}
                                                onChange={handleSignUpChange}
                                                size="sm"
                                                className={(!signUpValidation.email.isValid && signUpSubmitted) ? 'is-invalid' : ''}
                                            />
                                            {(!signUpValidation.email.isValid && signUpSubmitted) && (
                                                <span className="position-absolute end-0 top-50 translate-middle-y pe-2 text-danger">
                                                    <i className="bi bi-exclamation-circle"></i>
                                                </span>
                                            )}
                                        </div>
                                        {(!signUpValidation.email.isValid && signUpSubmitted) && (
                                            <div className="invalid-feedback d-block fw-bold">{signUpValidation.email.message}</div>
                                        )}
                                    </Form.Group>

                                    <Form.Group className="mb-2 position-relative">
                                        <Form.Label className="mb-0 fw-bold small" style={{ color: '#5e5873' }}>Password</Form.Label>
                                        <div className="position-relative d-flex align-items-center auth-password-field">
                                            <Form.Control
                                                type={showSignUpPassword ? 'text' : 'password'}
                                                name="password"
                                                value={signUpForm.password}
                                                onChange={(e) => {
                                                    // Prevent spaces in password
                                                    const value = e.target.value.replace(/\s/g, '');
                                                    handleSignUpChange({ target: { name: 'password', value } });
                                                }}
                                                size="sm"
                                                className={`auth-password-input ${(!signUpValidation.password.isValid && signUpSubmitted) ? 'is-invalid' : ''}`}
                                            />
                                            {/* {(!signUpValidation.password.isValid && signUpSubmitted) && (
                                                <span className="position-absolute end-0 top-50 translate-middle-y pe-2 text-danger">
                                                    <i className="bi bi-exclamation-circle"></i>
                                                </span>
                                            )} */}
                                            <div
                                                onClick={() => setShowSignUpPassword(!showSignUpPassword)}
                                                className="auth-password-toggle cursor-pointer"
                                            >
                                                {showSignUpPassword ? <Eye size={16} /> : <EyeOff size={16} />}
                                            </div>
                                        </div>
                                        {(!signUpValidation.password.isValid && signUpSubmitted) && (
                                            <div className="invalid-feedback d-block fw-bold">{signUpValidation.password.message}</div>
                                        )}
                                    </Form.Group>

                                    <Form.Group className="mb-2 position-relative">
                                        <Form.Label className="mb-0 fw-bold small" style={{ color: '#5e5873' }}>Confirm Password</Form.Label>
                                        <div className="position-relative d-flex align-items-center auth-password-field">
                                            <Form.Control
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                name="confirmPassword"
                                                value={signUpForm.confirmPassword}
                                                onChange={(e) => {
                                                    // Prevent spaces in confirm password
                                                    const value = e.target.value.replace(/\s/g, '');
                                                    handleSignUpChange({ target: { name: 'confirmPassword', value } });
                                                }}
                                                size="sm"
                                                className={`auth-password-input ${(!signUpValidation.confirmPassword.isValid && signUpSubmitted) ? 'is-invalid' : ''}`}
                                            />
                                            {/* {(!signUpValidation.confirmPassword.isValid && signUpSubmitted) && (
                                                <span className="position-absolute end-0 top-50 translate-middle-y pe-2 text-danger">
                                                    <i className="bi bi-exclamation-circle"></i>
                                                </span>
                                            )} */}
                                            <div
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                className="auth-password-toggle cursor-pointer"
                                            >
                                                {showConfirmPassword ? <Eye size={16} /> : <EyeOff size={16} />}
                                            </div>
                                        </div>
                                        {(!signUpValidation.confirmPassword.isValid && signUpSubmitted) && (
                                            <div className="invalid-feedback d-block fw-bold">{signUpValidation.confirmPassword.message}</div>
                                        )}
                                    </Form.Group>

                                    <Form.Group className="mb-1">
                                        <Form.Label className="mb-1 fw-bold small" style={{ color: '#5e5873' }}>Register As</Form.Label>
                                        <div className="d-flex justify-content-around flex-wrap">
                                            {(roles ?? []).length > 0 ? (
                                                (roles ?? [])
                                                    .filter(user => user.type_id !== 1)
                                                    .map((user, idx) => (
                                                        <div
                                                            key={idx}
                                                            className="d-inline-block"
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => handleSignUpChange({
                                                                target: {
                                                                    name: 'usertype_id',
                                                                    value: user.type_id.toString()
                                                                }
                                                            })}
                                                        >
                                                            <Form.Check
                                                                key={idx}
                                                                inline
                                                                label={user.type_name}
                                                                type="radio"
                                                                name="usertype_id"
                                                                value={user.type_id}
                                                                checked={signUpForm.usertype_id === user.type_id.toString()}
                                                                onChange={handleSignUpChange}
                                                                className={`small fw-bold ${!signUpValidation.role.isValid ? 'text-danger' : ''}`}
                                                            />
                                                        </div>
                                                    ))
                                            ) : (
                                                <div className="text-muted small">Loading...</div>
                                            )}
                                        </div>
                                        {!signUpValidation.role.isValid && (
                                            <div className="text-danger small mt-1">{signUpValidation.role.message}</div>
                                        )}
                                    </Form.Group>

                                    <Button type="submit" className="w-100 btn btn-primary btn-sm mt-2 fs-7">Sign up</Button>

                                    <p className="mt-3 mb-0 text-center small">
                                        Already have an account?{' '}
                                        <span
                                            className="text-primary fw-bold cursor-pointer"
                                            onClick={() => {
                                                // Clear signup form when switching back to login
                                                setSignUpForm({
                                                    firstName: '',
                                                    lastName: '',
                                                    gender: '',
                                                    phone_number: '',
                                                    phone_country_code: '+1',
                                                    dob: '',
                                                    email: '',
                                                    password: '',
                                                    confirmPassword: '',
                                                    role: 'User',
                                                    usertype_id: '2'
                                                });
                                                setSignUpSubmitted(false);
                                                // Reset sign-up validation state
                                                resetSignUpValidation();
                                                setShowResetPassword(false);
                                                setResetPasswordForm({ newPassword: '', confirmPassword: '' });
                                                setResetPasswordSubmitted(false);
                                                setShowNewPassword(false);
                                                setShowConfirmNewPassword(false);
                                                setShowOneTimeAccessForm(false);
                                                setOneTimeAccessForm({ email: '' });
                                                setOneTimeAccessSubmitted(false);
                                                setOneTimeAccessValidation({ email: { isValid: true, message: '' } });
                                                setShowErrorReportForm(false);
                                                setIsSignUp(false);
                                                navigate('/');
                                            }}
                                        >
                                            Sign in instead
                                        </span>
                                    </p>
                                </Form>
                            </div>
                        ) : showResetPassword ? (
                            <>
                                <div className="text-center mb-4">
                                    <div className="mb-3 d-flex align-items-center justify-content-start">
                                        <h4 className="mb-0 fw-bold me-2" style={{ color: '#5e5873' }}>Reset Password</h4>
                                        <i className="fa fa-lock" style={{ fontSize: '25px', color: '#ffa500' }}></i>
                                    </div>

                                </div>

                                <Form onSubmit={handleResetPasswordSubmit}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="mb-0 fw-bold small" style={{ color: '#5e5873' }}>New Password</Form.Label>
                                        <div className="position-relative auth-password-field">
                                            <Form.Control
                                                type={showNewPassword ? 'text' : 'password'}
                                                name="newPassword"
                                                value={resetPasswordForm.newPassword}
                                                onChange={(e) => {
                                                    const value = e.target.value.replace(/\s/g, '');
                                                    handleResetPasswordChange({ target: { name: 'newPassword', value } });
                                                }}
                                                onPaste={(e) => {
                                                    e.preventDefault();
                                                    const pastedText = (e.clipboardData.getData('text') || '').replace(/\s/g, '');
                                                    const input = e.target;
                                                    const current = resetPasswordForm.newPassword;
                                                    const start = input.selectionStart ?? current.length;
                                                    const end = input.selectionEnd ?? current.length;
                                                    const nextValue = `${current.slice(0, start)}${pastedText}${current.slice(end)}`;
                                                    handleResetPasswordChange({ target: { name: 'newPassword', value: nextValue } });
                                                }}
                                                className={`auth-password-input bg-white border ${(!resetPasswordValidation.newPassword.isValid && resetPasswordSubmitted) ? 'is-invalid' : ''}`}
                                                size="sm"
                                            // placeholder="Enter New Password"
                                            />
                                            {resetPasswordForm.newPassword.length > 0 && (
                                                <div
                                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                                    className="auth-password-toggle cursor-pointer"
                                                >
                                                    {showNewPassword ? <Eye size={16} /> : <EyeOff size={16} />}
                                                </div>
                                            )}
                                        </div>
                                        {(!resetPasswordValidation.newPassword.isValid && resetPasswordSubmitted) && (
                                            <div className="invalid-feedback d-block fw-bold">{resetPasswordValidation.newPassword.message}</div>

                                            //     <>
                                            //     <div className="password-error">
                                            //         {!resetPasswordForm.newPassword ? "New password is required" : "Password must meet the requirements below:"}
                                            //     </div>
                                            //     {resetPasswordForm.newPassword.length > 0 && (
                                            //         <div className="password-pattern-error">
                                            //             Must have at least 1 number, 1 special character, 1 lowercase and 1 uppercase character.
                                            //         </div>
                                            //     )}
                                            // </>)}
                                        )}
                                    </Form.Group>

                                    <Form.Group className="mb-4">
                                        <Form.Label className="mb-0 fw-bold small" style={{ color: '#5e5873' }}>Confirm Password</Form.Label>
                                        <div className="position-relative auth-password-field">
                                            <Form.Control
                                                type={showConfirmNewPassword ? 'text' : 'password'}
                                                name="confirmPassword"
                                                value={resetPasswordForm.confirmPassword}
                                                onChange={(e) => {
                                                    const value = e.target.value.replace(/\s/g, '');
                                                    handleResetPasswordChange({ target: { name: 'confirmPassword', value } });
                                                }}
                                                onPaste={(e) => {
                                                    e.preventDefault();
                                                    const pastedText = (e.clipboardData.getData('text') || '').replace(/\s/g, '');
                                                    const input = e.target;
                                                    const current = resetPasswordForm.confirmPassword;
                                                    const start = input.selectionStart ?? current.length;
                                                    const end = input.selectionEnd ?? current.length;
                                                    const nextValue = `${current.slice(0, start)}${pastedText}${current.slice(end)}`;
                                                    handleResetPasswordChange({ target: { name: 'confirmPassword', value: nextValue } });
                                                }}
                                                className={`auth-password-input bg-white border ${(!resetPasswordValidation.confirmPassword.isValid && resetPasswordSubmitted) ? 'is-invalid' : ''}`}
                                                size="sm"
                                            // placeholder="Enter Confirm Password"
                                            />
                                            {resetPasswordForm.confirmPassword.length > 0 && (
                                                <div
                                                    onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                                                    className="auth-password-toggle cursor-pointer"
                                                >
                                                    {showConfirmNewPassword ? <Eye size={16} /> : <EyeOff size={16} />}
                                                </div>
                                            )}
                                        </div>
                                        {(!resetPasswordValidation.confirmPassword.isValid && resetPasswordSubmitted) && (
                                            <div className="invalid-feedback d-block fw-bold">{resetPasswordValidation.confirmPassword.message}</div>

                                            //     <div className="password-error">
                                            //     {!resetPasswordForm.confirmPassword ? "Confirm password is required" : "Passwords do not match"}
                                            // </div>)}
                                        )}
                                    </Form.Group>

                                    <Button
                                        type="submit"
                                        className="w-100 btn btn-primary btn-sm mt-2 fs-7"
                                        disabled={isLoading}
                                        onClick={handleResetPasswordSubmit}
                                    >
                                        {isLoading ? 'Setting Password...' : 'Set New Password'}
                                    </Button>

                                    <div className="text-center mt-3">
                                        <span
                                            className="text-primary small fw-bold cursor-pointer d-flex align-items-center justify-content-center"
                                            onClick={() => {
                                                setShowResetPassword(false);
                                                setResetPasswordForm({ newPassword: '', confirmPassword: '' });
                                                setResetPasswordSubmitted(false);
                                                setShowNewPassword(false);
                                                setShowConfirmNewPassword(false);
                                                navigate('/');
                                            }}
                                        >
                                            <i className="fa fa-arrow-left me-2"></i>
                                            Back to login
                                        </span>
                                    </div>
                                </Form>
                            </>
                        ) : showAccessReports ? (
                            <>
                                <div>
                                    <h3 className="mb-3 fs-8" style={{ color: '#5e5873', fontWeight: 700, fontSize: 15 }}>
                                        Welcome to Acme Health! <span role="img" aria-label="wave">👋</span>
                                    </h3>
                                    <p className="fw mb-3 small" style={{ color: '#5e5873' }}>
                                        View shared reports using an access code and the patient's date of birth or email
                                    </p>
                                    <Form onSubmit={handleViewSharedReports}>
                                        {validationError && (
                                            <div className="alert alert-danger mb-3" role="alert">
                                                {validationError}
                                            </div>
                                        )}
                                        <Form.Group className="mb-3">
                                            <Form.Label className="mb-0 fw-bold small" style={{ color: '#5e5873' }}>Access Code</Form.Label>
                                            <Form.Control
                                                className="bg-white border"
                                                type="text"
                                                name="share_code"
                                                value={sharedReportsForm.share_code}
                                                onChange={handleSharedReportsChange}
                                                placeholder="Enter Access Code"
                                                size="sm"
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="mb-0 fw-bold small" style={{ color: '#5e5873' }}>Patient's Date of Birth</Form.Label>
                                            <div className="position-relative">
                                                <Flatpickr
                                                    value={sharedReportsForm.dob}
                                                    className="form-control bg-white border"
                                                    onChange={(date) => handleSharedReportsChange({ target: { name: 'dob', value: date } })}
                                                    placeholder="mm/dd/yyyy"
                                                    options={{
                                                        dateFormat: "m/d/Y",
                                                        maxDate: new Date(),
                                                        allowInput: true,
                                                        clickOpens: true,
                                                        theme: "material_green",
                                                        disable: [
                                                            function (date) {
                                                                return date > new Date();
                                                            }
                                                        ]
                                                    }}
                                                />
                                                <span
                                                    style={{ position: "absolute", right: "10px", top: "0px" }}
                                                    onClick={() => document.querySelector(".flatpickr-input").focus()}
                                                >
                                                    <Calendar size={20} style={{ color: "#000" }} />
                                                </span>
                                            </div>
                                        </Form.Group>
                                        <div className="text-center mb-3 fw-bold" style={{ color: '#5e5873' }}>OR</div>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="mb-0 fw-bold small" style={{ color: '#5e5873' }}>Patient's Email Address</Form.Label>
                                            <Form.Control
                                                className="bg-white border"
                                                type="email"
                                                name="email_address"
                                                value={sharedReportsForm.email_address}
                                                onChange={handleSharedReportsChange}
                                                placeholder="Enter Email"
                                                size="sm"
                                            />
                                        </Form.Group>
                                        <Button
                                            type="submit"
                                            className="w-100 btn btn-primary btn-sm mt-2 fs-5"
                                            disabled={isLoading}
                                            onClick={handleViewSharedReports}
                                        >
                                            {isLoading ? 'Validating...' : 'View Shared Reports'}
                                        </Button>
                                        <div className="text-center mt-3">
                                            <span
                                                className="text-primary small fw-bold cursor-pointer"
                                                onClick={() => {
                                                    setShowAccessReports(false);
                                                    setValidationError('');
                                                    setSharedReportsForm({
                                                        share_code: '',
                                                        dob: '',
                                                        email_address: '',
                                                        usertype_id: '',
                                                    });
                                                    navigate('/');
                                                }}
                                            >
                                                Back to login
                                            </span>
                                        </div>
                                    </Form>
                                </div>
                            </>
                        ) : showOneTimeAccessForm ? (
                            <>
                                <div>
                                    <h3 className="mb-3 fs-8" style={{ color: '#5e5873', fontWeight: 700, fontSize: 15 }}>
                                        Request one-time access link <span role="img" aria-label="link">🔗</span>
                                    </h3>
                                    <p className="fw mb-3 small" style={{ color: '#5e5873' }}>
                                        Enter your email and one-time access link will be sent to your email address
                                    </p>
                                    <Form onSubmit={handleOneTimeAccessSubmit}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="mb-0 fw-bold small" style={{ color: '#5e5873' }}>Email</Form.Label>
                                            <div className="position-relative">
                                                <Form.Control
                                                    type="email"
                                                    name="email"
                                                    value={oneTimeAccessForm.email}
                                                    onChange={handleOneTimeAccessChange}
                                                    placeholder="Enter your email address"
                                                    size="sm"
                                                    className={`bg-white border ${(!oneTimeAccessValidation.email.isValid && oneTimeAccessSubmitted) ? 'is-invalid' : ''}`}
                                                />
                                                {(!oneTimeAccessValidation.email.isValid && oneTimeAccessSubmitted) && (
                                                    <span className="position-absolute end-0 top-50 translate-middle-y pe-2 text-danger">
                                                        <i className="bi bi-exclamation-circle"></i>
                                                    </span>
                                                )}
                                            </div>
                                            {(!oneTimeAccessValidation.email.isValid && oneTimeAccessSubmitted) && (
                                                <div className="invalid-feedback d-block fw-bold">{oneTimeAccessValidation.email.message}</div>
                                            )}
                                        </Form.Group>
                                        <Button
                                            type="submit"
                                            className="w-100 btn btn-primary btn-sm mt-2 fs-5"
                                            disabled={isLoading}
                                        >
                                            {isLoading ? 'Sending...' : 'Send one-time access link'}
                                        </Button>
                                        <div className="text-center mt-3">
                                            <span
                                                className="text-primary small fw-bold cursor-pointer d-flex align-items-center justify-content-center"
                                                onClick={() => {
                                                    setShowOneTimeAccessForm(false);
                                                    setOneTimeAccessForm({ email: '' });
                                                    setOneTimeAccessSubmitted(false);
                                                    setOneTimeAccessValidation({ email: { isValid: true, message: '' } });
                                                    navigate('/');
                                                }}
                                            >
                                                <i className="fa fa-arrow-left me-2"></i>
                                                Back to login
                                            </span>
                                        </div>
                                    </Form>
                                </div>
                            </>
                        ) : showErrorReportForm ? (
                            <>
                                <div>
                                    <h3 className="mb-4 fw-bold" style={{ color: '#5e5873', fontSize: '18px' }}>
                                        Secure Login
                                    </h3>
                                    <div className="mb-4 pt-2 rounded" >
                                        {/* style={{ backgroundColor: '#fff5f5', border: '1px solid #fed7d7' }} */}
                                        <p className="mb-0 fw-bold pmr-form-error-text">
                                            {expireMessage}
                                        </p>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center mt-4">
                                        <span
                                            className="text-primary fw-bold cursor-pointer d-flex align-items-center"
                                            style={{ fontSize: '14px' }}
                                            onClick={() => {
                                                setShowErrorReportForm(false);
                                                navigate('/');
                                            }}
                                        >
                                            <i className="fa fa-arrow-left me-2"></i>
                                            Back to login
                                        </span>
                                        <span
                                            className="text-primary fw-bold cursor-pointer d-flex align-items-center"
                                            style={{ fontSize: '14px' }}
                                            onClick={() => {
                                                setShowErrorReportForm(false);
                                                setShowOneTimeAccessForm(true);
                                            }}
                                        >
                                            Request one-time access link again
                                            <i className="fa fa-arrow-right ms-2"></i>
                                        </span>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div >
                                    <h3>Welcome to Acme Health! 👋</h3>
                                    <p className="subtitle">Please sign-in to your account</p>
                                    <Form autoComplete="on" onSubmit={handleLoginSubmit}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Email</Form.Label>
                                            <div className="auth-email-field">
                                            <Form.Control
                                                ref={loginEmailRef}
                                                type="text"
                                                name="username"
                                                autoComplete="username"
                                                autoCorrect="off"
                                                autoCapitalize="off"
                                                spellCheck={false}
                                                value={loginForm.user_name}
                                                placeholder=""
                                                onAnimationStart={handleLoginAutofillAnimation}
                                                onChange={(e) => {
                                                    // Prevent spaces in email
                                                    const value = e.target.value.replace(/\s/g, '');
                                                    handleLoginChange({ target: { name: 'user_name', value } });
                                                }}
                                                onPaste={(e) => {
                                                    e.preventDefault();
                                                    const pastedText = e.clipboardData.getData('text').replace(/\s/g, '');
                                                    handleLoginChange({ target: { name: 'user_name', value: pastedText } });
                                                }}
                                                size="sm"
                                                className={`auth-email-input ${showEmailPasskeyIcon ? 'auth-email-input--passkey' : ''} ${(!loginValidation.email.isValid && (loginSubmitted || loginValidation.email.message)) ? 'is-invalid' : ''}`}
                                            />
                                            {showEmailPasskeyIcon && (
                                                <button
                                                    type="button"
                                                    className={`auth-passkey-icon ${(!loginValidation.email.isValid && (loginSubmitted || loginValidation.email.message)) ? 'auth-passkey-icon--invalid' : ''}`}
                                                    onClick={handlePasskeyLogin}
                                                    disabled={isLoading || passkeyBusy}
                                                    aria-label="Sign in with passkey"
                                                    data-tooltip="Sign in with passkey"
                                                >
                                                    <PasskeyPersonKeyIcon />
                                                </button>
                                            )}
                                            </div>
                                            {(!loginValidation.email.isValid && (loginSubmitted || loginValidation.email.message)) && (
                                                <div className="invalid-feedback d-block" style={{ color: '#ef4444', fontSize: '0.75rem', fontWeight: '400' }}>{loginValidation.email.message}</div>
                                            )}
                                        </Form.Group>

                                        <Form.Group className="mb-3 position-relative">
                                            <Form.Label>Password</Form.Label>
                                            <div className="position-relative d-flex align-items-center auth-password-field">
                                                <Form.Control
                                                    ref={loginPasswordRef}
                                                    type={showPassword ? 'text' : 'password'}
                                                    name="password"
                                                    autoComplete="current-password"
                                                    autoCorrect="off"
                                                    autoCapitalize="off"
                                                    spellCheck={false}
                                                    value={loginForm.password}
                                                    placeholder=""
                                                    onAnimationStart={handleLoginAutofillAnimation}
                                                    onChange={(e) => {
                                                        const value = e.target.value.replace(/\s/g, '');
                                                        handleLoginChange({ target: { name: 'password', value } });
                                                    }}
                                                    onPaste={(e) => {
                                                        e.preventDefault();
                                                        const pastedText = (e.clipboardData.getData('text') || '').replace(/\s/g, '');
                                                        const input = e.target;
                                                        const start = input.selectionStart ?? loginForm.password.length;
                                                        const end = input.selectionEnd ?? loginForm.password.length;
                                                        const nextValue = `${loginForm.password.slice(0, start)}${pastedText}${loginForm.password.slice(end)}`;
                                                        handleLoginChange({ target: { name: 'password', value: nextValue } });
                                                    }}
                                                    className={`auth-password-input ${(!loginValidation.password.isValid && loginSubmitted) ? 'is-invalid' : ''}`}
                                                />
                                                <div
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    className="auth-password-toggle cursor-pointer"
                                                >
                                                    {showPassword ? <Eye size={16} /> : <EyeOff size={16} />}
                                                </div>
                                            </div>
                                            {(!loginValidation.password.isValid && loginSubmitted) && (
                                                <div className="invalid-feedback d-block" style={{ color: '#ef4444', fontSize: '0.75rem', fontWeight: '400' }}>{loginValidation.password.message}</div>
                                            )}
                                            {(!loginValidation.credentials.isValid && loginSubmitted && !/passkey/i.test(loginValidation.credentials.message || '')) && (
                                                <div className="invalid-feedback d-block" style={{ color: '#ef4444', fontSize: '0.75rem', fontWeight: '400' }}>{loginValidation.credentials.message}</div>
                                            )}
                                        </Form.Group>

                                        <Form.Group className="d-flex align-items-center mb-3">
                                            <Form.Check
                                                type="checkbox"
                                                id="rememberMe"
                                                label="Remember Me"
                                                className="mb-0"
                                                checked={rememberMe}
                                                onChange={(e) => {
                                                    const isChecked = e.target.checked;
                                                    setRememberMe(isChecked);
                                                    if (!isChecked) {
                                                        // Clear stored credentials when unchecked
                                                        localStorage.removeItem('rememberedCredentials');
                                                    }
                                                }}
                                            />
                                        </Form.Group>

                                        <div className="d-flex gap-3 mb-3 justify-content-center">
                                            <Button type="submit" className="btn btn-primary">
                                                Login
                                            </Button>
                                            <Button
                                                type="button"
                                                className="btn btn-signup-gray"
                                                onClick={() => {
                                                    setSignUpForm({
                                                        firstName: '',
                                                        lastName: '',
                                                        gender: '',
                                                        phone_number: '',
                                                        phone_country_code: '+1',
                                                        dob: '',
                                                        email: '',
                                                        password: '',
                                                        confirmPassword: '',
                                                        role: 'User',
                                                        usertype_id: '2'
                                                    });
                                                    setSignUpSubmitted(false);
                                                    // Reset sign-up validation state
                                                    resetSignUpValidation();
                                                    setShowResetPassword(false);
                                                    setResetPasswordForm({ newPassword: '', confirmPassword: '' });
                                                    setResetPasswordSubmitted(false);
                                                    setShowNewPassword(false);
                                                    setShowConfirmNewPassword(false);
                                                    setShowOneTimeAccessForm(false);
                                                    setOneTimeAccessForm({ email: '' });
                                                    setOneTimeAccessSubmitted(false);
                                                    setOneTimeAccessValidation({ email: { isValid: true, message: '' } });
                                                    setShowErrorReportForm(false);
                                                    setIsSignUp(true);
                                                    navigate('/signup');
                                                }}
                                            >
                                                Sign up
                                            </Button>
                                        </div>
                                        <PasskeyLoginButton
                                            onClick={handlePasskeyLogin}
                                            busy={isLoading || passkeyBusy}
                                            disabled={isLoading}
                                            supported={passkeySupported}
                                            supportMessage={passkeySupportMessage}
                                        />
                                        <div className="d-flex justify-content-between align-items-start login-footer-links">
                                            <span className="link-text" onClick={handleAccessReport}>Access reports using share code</span>
                                            <div className="d-flex flex-column align-items-end gap-1">
                                                <span
                                                    className="link-text blue"
                                                    onClick={() => handleEmailAction('access')}
                                                >
                                                    Send an email with a one-time access link
                                                </span>
                                                <span
                                                    className="link-text blue"
                                                    onClick={() => {
                                                        setIsSignUp(false);
                                                        setShowAccessReports(false);
                                                        handleEmailAction('reset');
                                                    }}
                                                >
                                                    Reset Password
                                                </span>
                                            </div>
                                        </div>
                                    </Form>
                                </div>
                            </>
                        )}
                    </Col>
                </Row>
            </Container>
            <Modal show={showSuccessModal} onHide={handleModalClose} centered backdrop="static" keyboard={false} dialogClassName="modal-warning">
                <Modal.Header className="pmr-modal-status-header border-0">
                    <div className="w-100 d-flex justify-content-center">
                        <MdCheckCircleOutline size={48} style={{ color: 'var(--pmr-status-success)' }} />
                    </div>
                </Modal.Header>

                <Modal.Body className="pmr-modal-status-body text-center">
                    {successMessage}
                </Modal.Body>
                <Modal.Footer className="pmr-modal-status-footer d-flex justify-content-center border-0">
                    <Button className="pmr-modal-warning-btn no-hover p-2 px-4" onClick={handleModalClose}>
                        OK
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showWarningModal} onHide={() => setShowWarningModal(false)} centered dialogClassName="modal-warning">
                <Modal.Header className="pmr-modal-status-header border-0">
                    <div className="w-100 d-flex justify-content-center">
                        <MdWarningAmber size={48} style={{ color: 'var(--pmr-status-warning)' }} />
                    </div>
                </Modal.Header>

                <Modal.Body className="pmr-modal-status-body text-center">
                    {warningMessage}
                </Modal.Body>
                <Modal.Footer className="pmr-modal-status-footer d-flex justify-content-center border-0">
                    <Button
                        className="pmr-modal-warning-btn no-hover p-2 px-4"
                        onClick={() => {
                            setShowWarningModal(false);
                            setWarningMessage('');
                        }}
                    >
                        OK
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered dialogClassName="modal-warning">
                <Modal.Header className="pmr-modal-status-header border-0">
                    <div className="w-100 d-flex justify-content-center">
                        <MdOutlineCancel size={48} style={{ color: 'var(--pmr-status-error)' }} />
                    </div>
                </Modal.Header>

                <Modal.Body className="pmr-modal-status-body text-center">
                    {errorMessage}
                </Modal.Body>
                <Modal.Footer className="pmr-modal-status-footer d-flex justify-content-center border-0">
                    <Button className="pmr-modal-warning-btn no-hover p-2 px-4" onClick={() => setShowErrorModal(false)}>OK</Button>
                </Modal.Footer>
            </Modal>

            <PasskeySetupModal
                show={showPasskeySetupModal}
                onSkip={handlePasskeySetupSkip}
                onSetup={handlePasskeySetupConfirm}
                busy={isLoading || passkeyBusy}
                error={passkeyError}
                supported={passkeySupported}
                supportMessage={passkeySupportMessage}
                title="Create a passkey?"
                description="Use your fingerprint, face, or screen lock to sign in next time — no password needed."
                confirmText="Create passkey"
                cancelText="Not now"
                cancelDisabled={pendingLoginSession ? false : undefined}
            />


        </div>
    );
};

export default Auth;
